import { FC, useState } from 'react';
import classNames from 'classnames';
import { Link } from '@/components';
import startCase from 'lodash/startCase';

import { NOT_DEFINED, SPECIAL_METHOD } from '@/constants';
import { useAnalytics, useUiStore } from '@/data';
import { useBundleSpecial } from '@/hooks';
import { CartLineItem, GProduct } from '@/types';
import { currencyFormat, getBogoSpecialURL } from '@/utils';
import { CartItemBadges } from './CartItemBadges';

import {
  Badge,
  Close,
  Counter,
  Heading,
  IconSvg,
  Image,
  Price,
  ProductProvider,
} from '@/components';

import style from './cartnavigation.module.scss';

interface CartBodyItemProps {
  product: CartLineItem;
}

const CartBodyItem: FC<CartBodyItemProps> = ({ product }) => {
  const [, setState] = useUiStore();
  const [closeBogo, setCloseBogo] = useState(false);
  const { measureProductClick } = useAnalytics();
  const { special } = useBundleSpecial({ id: product?.bundle_id });
  const bogo = {
    discount_id: special?.specialId ?? '',
    discount_title: special?.title ?? '',
  };

  const REVERSE_PROXY_LINK = process.env.NEXT_PUBLIC_REVERSE_PROXY_LINK ?? '';

  const trackingClick = (product: GProduct) =>
    measureProductClick(product, 'shopping cart');

  const v = product?.weightVariant;
  const selectedTierPrice = (product?.unitPricingTier?.price || 0) * 100;
  const isSpecial = (selectedTierPrice || v?.salePrice!) < v?.price!;
  const stockPhoto = product?.asset?.stock ?? false;

  const isSpecialBundleIncomplete = !special?.bundleComplete;
  const isBundleOrBogoSpecial = [
    SPECIAL_METHOD.BOGO,
    SPECIAL_METHOD.BUNDLE,
  ].includes(special?.method as SPECIAL_METHOD);

  const isBundleActive = isBundleOrBogoSpecial && isSpecialBundleIncomplete;

  const price = (
    <Price
      price={`$${currencyFormat(v?.price!)}`}
      salePrice={`$${currencyFormat(selectedTierPrice || v?.salePrice!)}`}
      special={isSpecial}
    />
  );

  return (
    <li
      className={classNames(style.cart__product, {
        [style.cart__product_active_bogo]: isBundleActive && !closeBogo,
      })}
    >
      <ProductProvider product={product}>
        <div
          className={classNames(style.cart__product_body, {
            [style.cart__product_body_active_bogo]:
              isBundleActive && !closeBogo,
          })}
        >
          <Link
            className={style.cart__item_image}
            href={`${product.url}?item=${product.id}`}
            onClick={() => {
              trackingClick(product);
              setState({ is_cart_open: false });
            }}
          >
            <figure>
              <Image
                alt={product?.name ?? 'Product image'}
                fallback={`${REVERSE_PROXY_LINK}/images/product-placeholder.svg`}
                height={72}
                src={product?.asset?.image ?? ''}
                style={{
                  width: '100%',
                  height: 'auto',
                }}
                width={72}
              />
            </figure>
            {stockPhoto && (
              <span className={style.product__in_stock}>Stock photo</span>
            )}
          </Link>
          <div className={style.cart__detail_item}>
            <div className={style.cart__detail_brand}>
              <p>
                <span>{product?.brand_name ?? NOT_DEFINED}</span> •{' '}
                {startCase(product.category as string) ||
                  startCase(NOT_DEFINED)}{' '}
              </p>
              <CartItemBadges
                badgeClass={style.cart__product_badge}
                containerClass={style.cart__product_badges}
                product={product}
              />
            </div>
            <Link
              className={style.cart__info_container}
              href={`${product.url}?item=${product.id}`}
              onClick={() => {
                trackingClick(product);
                setState({
                  is_cart_open: false,
                });
              }}
            >
              <Heading
                className={style.heading_level_4}
                level={4}
                title={product.name!}
              >
                {product.name}
              </Heading>
              <div className={style.price__container}>{price}</div>
            </Link>
            <div className={style.actions}>
              <Counter onShopping product={product} />
            </div>
          </div>
        </div>
        {isBundleActive && !closeBogo && (
          <Badge className={style.cart_item_bogo} href="/" key={product.id}>
            <figure>
              <IconSvg name="gift" />
            </figure>
            <div className={style.cart_item_bogo_message}>
              <span>You are on your way to a Deal!</span>
              <Link
                href={getBogoSpecialURL(bogo!)}
                onClick={() => {
                  setState({ is_cart_open: false });
                }}
              >
                Click here to complete
              </Link>
            </div>
            <Close
              className={style.cart_item_bogo_close}
              onClick={() => {
                setCloseBogo(true);
              }}
            />
          </Badge>
        )}
      </ProductProvider>
    </li>
  );
};

export { CartBodyItem };
